/**
 * Device activation — binding this physical terminal to a `devices` row.
 *
 * PRIMARY PATH — SELF-SERVICE CLAIM (`POST /v1/devices/claim`, routes/qr.ts):
 * the admin registers the device and reads the 6-character code out loud; the
 * person at the terminal types it in and the server answers with everything
 * the terminal needs to boot (mode, branch, station, terminal). The call is
 * UNAUTHENTICATED — the code IS the credential (single-use, rate-limited,
 * 24-hour expiry) — so a virgin terminal can pair itself with no manager
 * present. On success the binding is cached locally and the app reloads
 * straight into the provisioned shell.
 *
 * FALLBACK — MANAGER-LED (`POST /v1/devices/:id/activate`): a signed-in user
 * holding `device.manage` picks the device row (or pastes its id) and
 * activates it explicitly. Kept because a claim can fail legitimately — code
 * expired, device already ACTIVE — and re-registering takes an admin anyway.
 */

import { useMemo, useState } from 'react';
import { Badge, Button, Card, FormField, Input, Select, ErrorState, LoadingState } from '@sonauto/ui';
import { useAuth } from '../auth/AuthProvider';
import { useDeviceMode } from './DeviceModeProvider';
import { DEVICE_MODE_INFO, SELECTABLE_MODES, normalizeWireMode } from './mode';
import type { DeviceMode } from './mode';
import { deviceFingerprint } from '../lib/storage';
import { useAsync, errorMessage } from '../lib/hooks';
import type { Device, DevicesResponse, StationsResponse } from '../lib/types';

export function ActivationScreen({ onDone }: { readonly onDone: () => void }): React.JSX.Element {
  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4">
      <SelfClaimCard />
      <ManagerActivationCard onDone={onDone} />
      <p className="text-center">
        <Button variant="ghost" onClick={onDone}>
          Quay lại mà không kích hoạt
        </Button>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. Self-service claim — the normal path
// ---------------------------------------------------------------------------

function SelfClaimCard(): React.JSX.Element {
  const { api } = useAuth();
  const { setBinding } = useDeviceMode();

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [claimed, setClaimed] = useState<{ deviceMode: string; branchName: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const claim = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    const trimmedCode = code.trim().toUpperCase();
    const deviceName = name.trim() === '' ? `Thiết bị POS (${new Date().toLocaleDateString('vi-VN')})` : name.trim();
    if (trimmedCode.length < 4) {
      setError('Mã kích hoạt phải có ít nhất 4 ký tự.');
      return;
    }
    setBusy(true);
    try {
      const result = await api.org.claimDevice({
        activationCode: trimmedCode,
        deviceName,
        platform: typeof navigator === 'undefined' ? null : navigator.platform,
        fingerprint: deviceFingerprint(),
      });
      // The server's answer is authoritative: mode, branch and station were set
      // by the admin at registration. Cache it — this is what keeps the shell
      // correct on sessions that cannot read the device row.
      setBinding({
        deviceId: result.deviceId,
        deviceName,
        branchId: result.branchId,
        deviceMode: normalizeWireMode(result.deviceMode),
        stationId: result.stationId,
      });
      setClaimed({
        deviceMode: DEVICE_MODE_INFO[normalizeWireMode(result.deviceMode) ?? 'SERVICE_TERMINAL'].nameVi,
        branchName: result.branchName,
      });
      // Reload rather than re-render: the whole provider tree (auth branch,
      // outbox scope, mode resolution) must boot from the new binding.
      window.setTimeout(() => window.location.reload(), 900);
    } catch (cause: unknown) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  if (claimed !== null) {
    return (
      <Card title="Kích hoạt thành công">
        <p className="text-flow text-sm text-fg">
          Máy này giờ là <strong>{claimed.deviceMode}</strong> của chi nhánh <strong>{claimed.branchName}</strong>.
          Đang tải lại ứng dụng…
        </p>
        <LoadingState compact label="Đang khởi động giao diện mới…" />
      </Card>
    );
  }

  return (
    <Card
      title="Kích hoạt thiết bị bằng mã"
      description="Nhập mã 6 ký tự do quản trị viên cung cấp khi đăng ký thiết bị. Không cần quyền đặc biệt — mã chính là chìa khóa, dùng được đúng một lần và hết hạn sau 24 giờ."
    >
      <form onSubmit={(e) => void claim(e)} className="mt-2 space-y-4">
        <FormField label="Mã kích hoạt (6 ký tự)" required>
          {(field) => (
            <Input
              {...field}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              maxLength={16}
              autoCapitalize="characters"
              autoComplete="one-time-code"
              className="font-mono text-2xl tracking-[0.4em]"
              required
            />
          )}
        </FormField>

        <FormField label="Tên gợi nhớ cho máy này" hint="Ví dụ: “Quầy thu ngân 1”, “KDS bếp nóng”. Bỏ trống sẽ tự đặt.">
          {(field) => <Input {...field} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />}
        </FormField>

        {error !== null ? <ErrorState title="Không kích hoạt được" message={error} /> : null}

        <Button type="submit" size="touch" fullWidth loading={busy}>
          Kích hoạt thiết bị
        </Button>

        <p className="text-flow text-xs text-fg-subtle">
          Chế độ hiển thị (thu ngân / bếp / kho…), chi nhánh và station do quản trị viên chọn lúc đăng ký — máy sẽ tự
          vào đúng giao diện sau khi kích hoạt. Nếu mã báo “đã được kích hoạt” mà không phải bạn làm, hãy báo quản trị
          viên thu hồi thiết bị ngay.
        </p>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 2. Manager-led activation — the fallback
// ---------------------------------------------------------------------------

function ManagerActivationCard({ onDone }: { readonly onDone: () => void }): React.JSX.Element {
  const { api, me, permissions } = useAuth();
  const { setBinding } = useDeviceMode();
  const branchId = me?.session.activeBranchId ?? null;

  const canList = permissions.has('device.view');
  const canManage = permissions.has('device.manage');

  const [open, setOpen] = useState(false);

  const devices = useAsync<DevicesResponse>(
    (signal) => api.client.get<DevicesResponse>('/v1/devices', { query: { branchId: branchId ?? '' }, signal }),
    [branchId],
    { enabled: open && canList && branchId !== null },
  );

  const stations = useAsync<StationsResponse>(
    (signal) => api.org.stations({ branchId: branchId ?? '', activeOnly: true }, { signal }),
    [branchId],
    { enabled: open && branchId !== null },
  );

  const [deviceId, setDeviceId] = useState('');
  const [code, setCode] = useState('');
  const [mode, setMode] = useState<DeviceMode>('POS_CASHIER');
  const [stationId, setStationId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const pending = useMemo<Device[]>(
    () => (devices.data?.devices ?? []).filter((d) => d.activationStatus !== 'REVOKED'),
    [devices.data],
  );

  const selectedDevice = pending.find((d) => d.id === deviceId) ?? null;
  const needsStation = mode === 'KITCHEN_DISPLAY' || mode === 'BAR_DISPLAY';

  const activate = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    const trimmedId = deviceId.trim();
    const trimmedCode = code.trim().toUpperCase();
    if (trimmedId === '' || trimmedCode.length < 4) {
      setError('Cần mã thiết bị và mã kích hoạt (tối thiểu 4 ký tự).');
      return;
    }
    setBusy(true);
    try {
      await api.org.activateDevice(trimmedId, {
        activationCode: trimmedCode,
        fingerprint: deviceFingerprint(),
        platform: typeof navigator === 'undefined' ? null : navigator.platform,
      });
      setBinding({
        deviceId: trimmedId,
        deviceName: selectedDevice?.deviceName ?? `Thiết bị ${trimmedId.slice(-6)}`,
        branchId: selectedDevice?.branchId ?? branchId,
        // Prefer the mode the admin registered; fall back to the operator's
        // choice. This local copy keeps the shell correct until `GET
        // /v1/devices/me` can confirm it on the next signed-in boot.
        deviceMode: normalizeWireMode(selectedDevice?.deviceMode) ?? mode,
        stationId: selectedDevice?.stationId ?? (stationId === '' ? null : stationId),
      });
      onDone();
    } catch (cause: unknown) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <p className="text-center text-xs text-fg-subtle">
        Mã không còn dùng được (đã hết hạn hoặc đã kích hoạt)?{' '}
        <button type="button" className="underline" onClick={() => setOpen(true)}>
          Kích hoạt thủ công bởi quản lý
        </button>
      </p>
    );
  }

  return (
    <Card
      title="Kích hoạt thủ công (quản lý)"
      description="Cần quyền device.manage. Dùng khi mã tự kích hoạt đã hết hạn hoặc thiết bị cần gán lại."
    >
      {!canManage ? (
        <ErrorState
          title="Tài khoản này không được phép kích hoạt thủ công"
          message="Cần quyền device.manage. Hãy nhờ quản lý chi nhánh đăng nhập trên chính máy này."
        />
      ) : null}

      <form onSubmit={(e) => void activate(e)} className="mt-4 space-y-4">
        {canList ? (
          devices.initialLoading ? (
            <LoadingState label="Đang tải danh sách thiết bị…" compact />
          ) : (
            <FormField label="Thiết bị" hint="Danh sách thiết bị đã đăng ký của chi nhánh này.">
              {(field) => (
                <Select
                  {...field}
                  value={deviceId}
                  onChange={(e) => {
                    setDeviceId(e.target.value);
                    const found = pending.find((d) => d.id === e.target.value);
                    const wire = normalizeWireMode(found?.deviceMode);
                    if (wire !== null) setMode(wire);
                    if (found?.stationId != null) setStationId(found.stationId);
                  }}
                  placeholder="— Chọn thiết bị —"
                  options={pending.map((d) => ({
                    value: d.id,
                    label: `${d.deviceName} · ${DEVICE_MODE_INFO[normalizeWireMode(d.deviceMode) ?? 'POS_CASHIER'].nameVi} · ${d.activationStatus}`,
                  }))}
                />
              )}
            </FormField>
          )
        ) : (
          <FormField
            label="Mã thiết bị (device id)"
            required
            hint="Hiển thị cạnh mã kích hoạt trong ứng dụng Quản trị."
          >
            {(field) => <Input {...field} value={deviceId} onChange={(e) => setDeviceId(e.target.value)} required />}
          </FormField>
        )}

        <FormField label="Mã kích hoạt (6 ký tự)" required>
          {(field) => (
            <Input
              {...field}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              maxLength={16}
              autoCapitalize="characters"
              autoComplete="one-time-code"
              className="font-mono text-2xl tracking-[0.4em]"
              required
            />
          )}
        </FormField>

        <FormField
          label="Chế độ thiết bị"
          hint="Quyết định giao diện máy này hiển thị. Nếu thiết bị đã được đăng ký với một chế độ, chế độ đó được ưu tiên."
        >
          {(field) => (
            <Select
              {...field}
              value={mode}
              onChange={(e) => setMode(e.target.value as DeviceMode)}
              options={SELECTABLE_MODES.map((m) => ({ value: m, label: DEVICE_MODE_INFO[m].nameVi }))}
            />
          )}
        </FormField>
        <p className="text-flow -mt-2 text-sm text-fg-muted">{DEVICE_MODE_INFO[mode].descriptionVi}</p>

        {needsStation ? (
          <FormField label="Station hiển thị" hint="Bảng phiếu của station nào sẽ hiện trên màn hình này.">
            {(field) => (
              <Select
                {...field}
                value={stationId}
                onChange={(e) => setStationId(e.target.value)}
                placeholder="— Chọn station —"
                options={(stations.data?.stations ?? []).map((s) => ({
                  value: s.id,
                  label: `${s.name} (${s.stationType})`,
                }))}
              />
            )}
          </FormField>
        ) : null}

        {error !== null ? <ErrorState title="Không kích hoạt được" message={error} /> : null}

        <div className="flex items-center gap-3">
          <Button type="submit" size="touch" loading={busy} disabled={!canManage}>
            Kích hoạt thiết bị
          </Button>
        </div>
      </form>

      <p className="mt-3 text-center text-xs text-fg-subtle">
        <Badge tone="neutral" size="sm">
          Trạng thái
        </Badge>{' '}
        {devices.error !== null && canList ? 'Không tải được danh sách thiết bị.' : 'Sẵn sàng kích hoạt.'}
      </p>
    </Card>
  );
}
